import { z } from "zod";

import { baseApiResponse, baseApiResponseError } from "./BaseApiResponse";
import { Currencies } from "./Currency";

// export const validateUserRegister = z.object({
// 	code: z.number().optional(),
// 	data: z.object({
// 		errors: z
// 			.object({
// 				email: z.string().optional(),
// 				password: z.string().optional()
// 			})
// 			.or(z.object({}).array()),
// 		result: z.boolean()
// 	}),
// 	status: z.enum(["success", "error"])
// });
//
// export type ValidateUserRegisterResponse = z.infer<typeof validateUserRegister>;

export enum AccountTypes {
	TRADING = "TRADING",
	GAMING = "GAMING"
}

export enum VerificationDocumentStatuses {
	AWAITING_VALIDATION = "AwaitingValidation",
	FAILED_VALIDATION = "FailedValidation",
	VALIDATED = "Validated",
	EXPIRED = "Expired"
}

export const userProfile = z.object({
	idUser: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	email: z.string().email(),
	login: z.string(),
	countryCode: z.string(),
	currency: z.string(),
	gender: z.string(),
	idNumber: z.string(),
	postalCode: z.string(),
	city: z.string(),
	address: z.string(),
	birthDay: z.string(),
	birthMonth: z.string(),
	birthYear: z.string(),
	phoneAltCode: z.string(),
	phoneAltNumber: z.string(),
	extProfile: z
		.object({
			colorTheme: z.string()
		})
		.or(
			z.array(
				z.object({
					colorTheme: z.string()
				})
			)
		),
	registrationBonus: z.string(),
	phoneCode: z.string(),
	phoneNumber: z.string(),
	newEmail: z.string(),
	phoneVerified: z.string(),
	emailVerified: z.number(),
	ibanNumber: z.string(),
	branchCode: z.string(),
	swift: z.string(),
	bankName: z.string(),
	bankNameText: z.string(),
	affiliateSystem: z.string(),
	affiliateId: z.string(),
	affiliateClickId: z.string(),
	RestrictCasinoBonuses: z.number(),
	RestrictSportBonuses: z.number(),
	stateCode: z.string(),
	socketsData: z.string(),
	registrationIP: z.string(),
	fromPublicAccount: z.string(),
	bonusIdPublicAccount: z.string(),
	idIssueDate: z.string(),
	idExpirationDate: z.string(),
	idIssuer: z.string(),
	bankAddress: z.string(),
	validationLevel: z.string(),
	message: z.string(),
	walletAddress: z.string(),
	signature: z.string(),
	type: z.string(),
	cpf: z.string(),
	registrationURL: z.string(),
	nick: z.string(),
	smsAgree: z.boolean(),
	emailAgree: z.boolean(),
	verificationConditions: z.array(z.unknown())
});

export type UserProfileModel = z.infer<typeof userProfile>;

export const createProfileRequest = z.object({
	address: z.string(),
	birthDay: z.string(), // or z.number() if it's expected to be numeric
	birthMonth: z.string(), // or z.number() if it's expected to be numeric
	birthYear: z.string(), // or z.number() if it's expected to be numeric
	city: z.string(),
	code: z.string(),
	countryCode: z.string(),
	currency: z.nativeEnum(Currencies),
	email: z.string(),
	extProfile: z.object({
		nick: z.string().optional()
	}),
	firstName: z.string(),
	gender: z.literal("m").optional().or(z.literal("f").optional()).or(z.string().optional()),
	lastName: z.string(),
	login: z.string(),
	message: z.string(),
	password: z.string(),
	passwordRepeat: z.string(),
	phoneCode: z.string(),
	phoneNumber: z.string(),
	postalCode: z.string(),
	registrationPromoCode: z.string(),
	registrationBonus: z.string(),
	signature: z.string(),
	smsCode: z.string(),
	skipEmailVerification: z.boolean(),
	idNumber: z.string(),
	useJwt: z.number(), // If 0 is the default value, use a numeric schema
	walletAddress: z.string(),
	affiliateSystem: z.string(),
	affiliateId: z.string(),
	affiliateClickId: z.string(),
	cpf: z.string(),
	// CHECK FIELDS BELOW
	VerificationJobID: z.string(),
	VerificationSessionID: z.string(),
	ageConfirmed: z.boolean(),
	agreedWithTermsAndConditions: z.boolean().optional(),
	bankName: z.string().optional(),
	bankNameText: z.string().optional(),
	birthDate: z.string().optional(),
	branchCode: z.string().optional(),
	emailVerified: z.string().optional(),
	ibanNumber: z.string().optional(),
	idUser: z.string().optional(),
	newEmail: z.string().optional(),
	oddsStyle: z.string().optional(),
	phoneAltCode: z.string().optional(),
	phoneAltNumber: z.string().optional(),
	phoneVerified: z.string().optional(),
	registrationURL: z.string().optional(),
	swift: z.string().optional()
});

export type CreateProfileRequest = z.infer<typeof createProfileRequest>;

export const currentProfileResult = baseApiResponse.extend({
	data: userProfile
});

export type CurrentProfileResult = z.infer<typeof currentProfileResult>;

const FreeroundsResolver = z.object({
	IDMerchant: z.string(),
	Count: z.string(),
	Games: z.array(z.string()),
	ExpireDate: z.string(),
	BetLevel: z.string(),
	Coins: z.nullable(z.any()),
	AddDate: z.string(),
	Additional: z.nullable(z.any())
});

const socketsDataResolver = z.object({
	api: z.string(),
	token: z.string(),
	server2: z.string()
});

const tagsResolver = z.record(z.string());

const toSVersionResolver = z.object({
	AcceptDateTime: z.string(),
	ToSVersion: z.string()
});

const walletResolver = z.object({
	walletId: z.number(),
	balance: z.string(),
	availableWithdraw: z.string(),
	currency: z.string(),
	freerounds: z.array(z.any())
});

const loyaltyResolver = z.object({
	IDUser: z.string(),
	Balance: z.string(),
	Login: z.string(),
	Country: z.string(),
	Language: z.string(),
	Currency: z.string(),
	Level: z.string(),
	DuplicateLevel: z.string(),
	Points: z.string(),
	NextLevelPoints: z.string(),
	TotalPoints: z.string(),
	TotalBets: z.string(),
	CheckDate: z.string(),
	ForbidBonuses: z.string(),
	ForbidLoyaltyPoints: z.string(),
	ForbidTournaments: z.string(),
	DepositsCount: z.string(),
	BonusRestrictions: z.object({
		RestrictCasinoBonuses: z
			.object({
				State: z.number(),
				IDUser: z.string(),
				Time: z.string()
			})
			.or(z.string()),
		RestrictSportBonuses: z
			.object({
				State: z.number(),
				IDUser: z.string(),
				Time: z.string()
			})
			.or(z.string()),
		RestrictPokerBonuses: z
			.object({
				State: z.number(),
				IDUser: z.string(),
				Time: z.string()
			})
			.or(z.string())
	}),
	Verificated: z.string(),
	IDCategory: z.string(),
	AutoCategoryID: z.string().nullable(),
	Wallets: z.record(walletResolver).or(z.string()),
	LoyaltyID: z.string(),
	ConfirmPoints: z.string(),
	LevelName: z.record(z.string()),
	LevelCoef: z.string(),
	Block: z.number(),
	ActiveBonuses: z
		.record(
			z.object({
				IDLoyaltyBonuses: z.string(),
				IDMerchants: z.array(z.number()),
				CategoriesBlackList: z.array(z.number()),
				CategoriesWhiteList: z.array(z.any()),
				GamesBlackList: z.array(z.any()),
				GamesWhiteList: z.array(z.any())
			})
		)
		.or(z.array(z.any())),
	LevelUp: z.boolean(),
	BonusesBalance: z.record(
		z.object({
			Balance: z.string(),
			IDLoyaltyBonuses: z.string(),
			Merchants: z.array(z.string()),
			RestrictSettings: z.object({
				CategoriesBlackList: z.array(z.number())
			}),
			SportSettings: z.nullable(z.any()),
			StrictDistribute: z.string(),
			DistributeBet: z.string()
		})
	)
});

export const userInfo = z.object({
	balance: z.number(),
	email: z.string(),
	firstSession: z.boolean(),
	idUser: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	status: z.number(),
	availableWithdraw: z.number(),
	pincode: z.string(),
	currency: z.string(),
	emailHash: z.string(),
	category: z.string(),
	Freerounds: z.array(FreeroundsResolver).optional(),
	affiliateID: z.string(),
	LockExpiresAt: z.string(),
	nick: z.string(),
	NotifyKYCQuestionnaire: z.boolean(),
	socketsData: socketsDataResolver.or(z.string()),
	userSessionLimit: z.boolean(),
	Tags: tagsResolver.or(tagsResolver.array()),
	validationLevel: z.string(),
	displayConsolidatedBalanceToStreamer: z.boolean(),
	streamWheelOwner: z.boolean().or(z.number()),
	streamWheelsParticipant: z.array(z.number()),
	transfersAllowed: z.boolean(),
	toSVersion: toSVersionResolver,
	toSWlcVersion: z.string(),
	wallets: z.record(walletResolver),
	notify2FAGoogle: z.boolean(),
	enabled2FAGoogle: z.boolean(),
	allowAccessToAddStreamWheel: z.boolean(),
	isEmailValid: z.boolean(),
	blockByLocation: z.boolean(),
	loyalty: loyaltyResolver
});

export type UserModel = z.infer<typeof userInfo>;

export const userDocumentValidationResolver = z.object({
	ID: z.number(),
	IDUser: z.number(),
	IDVerifier: z.number(),
	Status: z.nativeEnum(VerificationDocumentStatuses),
	StatusDescription: z.string(),
	AddDate: z.string(),
	UpdateDate: z.nullable(z.string()),
	FileName: z.string(),
	FileType: z.string(),
	DocType: z.string(),
	Description: z.string(),
	ExpiryDate: z.nullable(z.string()),
	StatusDescriptionPrivate: z.string(),
	AdditionalParams: z.array(z.any()),
	Type: z.record(z.string()),
	Link: z.string(),
	DownloadLink: z.string()
});

export type UserDocumentValidationModel = z.infer<typeof userDocumentValidationResolver>;

export const currentUserResult = baseApiResponse.extend({
	data: userInfo
});

export type CurrentUserResult = z.infer<typeof currentUserResult>;

export const userDocumentValidationResult = baseApiResponse.extend({
	data: userDocumentValidationResolver.array()
});

export type UserDocumentValidationResult = z.infer<typeof userDocumentValidationResult>;

export const userDocumentTypeResolver = z.object({
	ID: z.string(),
	Name: z.string(),
	PID: z.string(),
	TypeKey: z.string()
});

export type UserDocumentTypeModel = z.infer<typeof userDocumentTypeResolver>;

export const userDocumentTypesResult = baseApiResponse.extend({
	data: userDocumentTypeResolver.array()
});

export type UserDocumentTypesResult = z.infer<typeof userDocumentTypesResult>;

export const validateDocumentResult = baseApiResponse.extend({
	data: z
		.object({
			errors: baseApiResponseError,
			result: z.boolean()
		})
		.or(z.string())
});

export type ValidateDocumentResult = z.infer<typeof validateDocumentResult>;

export const getAuthenticatorSettingsResult = baseApiResponse.extend({
	data: z.object({
		path: z.string(),
		secret: z.string()
	})
});

export type GetAuthenticatorSettingsResult = z.infer<typeof getAuthenticatorSettingsResult>;
